import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  increment,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getOrCreateConversation } from '../lib/chat';
import { REPORT_REASONS } from '../lib/constants';
import {
  coverPhoto,
  formatPrice,
  formatMoney,
  getPreviousPrice,
  isHighDemand,
  listingFreshnessBadge,
  listingFreshnessLabel,
  listingStatusLabel,
  orderedPhotos,
  relativeTime,
  shareUrl,
} from '../lib/format';
import { track } from '../lib/analytics';
import { approximateTownCenter, formatDistance, haversineMiles } from '../lib/geo';
import { googleMapsDirectionsUrl } from '../lib/maps';
import { useSheets } from '../hooks/useSheets';
import Empty from '../components/Empty';
import Icon from '../components/Icon';
import ImageViewer from '../components/ImageViewer';
import PickupMap from '../components/PickupMap';
import Sheet from '../components/Sheet';

export default function DetailPage({ user, browseLocation, onRequestAuth }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { openMessages } = useSheets();
  const [item, setItem] = useState(undefined);
  const [saved, setSaved] = useState(false);
  const [liked, setLiked] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shareHint, setShareHint] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!id) return undefined;
    return onSnapshot(doc(db, 'listings', id), (snap) => {
      setItem(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
  }, [id]);

  useEffect(() => {
    if (!user || !id) return undefined;
    const unsubSaved = onSnapshot(doc(db, 'users', user.uid, 'saved', id), (s) =>
      setSaved(s.exists()),
    );
    const unsubLiked = onSnapshot(doc(db, 'users', user.uid, 'likes', id), (s) =>
      setLiked(s.exists()),
    );
    return () => {
      unsubSaved();
      unsubLiked();
    };
  }, [user, id]);

  useEffect(() => {
    if (!user || !id || !item) return;
    if ((item.status === 'sold' || item.status === 'archived') && item.sellerId !== user.uid) return;
    updateDoc(doc(db, 'listings', id), { viewCount: increment(1) }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id, item?.id]);

  useEffect(() => {
    if (!id || !item?.id) return;
    track('view_item', {
      item_id: id,
      item_name: item.title || '',
      item_category: item.category || '',
      price: item.price ?? 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, item?.id]);

  const photos = useMemo(() => (item ? orderedPhotos(item) : []), [item]);
  const isOwner = user?.uid && item?.sellerId === user.uid;
  const isSold = item?.status === 'sold' && !isOwner;
  const isUnavailable = (item?.status === 'sold' || item?.status === 'archived') && !isOwner;
  const unavailableMessage = isSold
    ? 'You missed it. This item has been sold.'
    : isUnavailable
      ? 'This item is no longer available.'
      : '';

  useEffect(() => {
    if (!user || !id || !saved) return;
    if (item === null || isUnavailable) {
      deleteDoc(doc(db, 'users', user.uid, 'saved', id)).catch(() => {});
    }
  }, [user, id, saved, item, isUnavailable]);

  const distance = useMemo(() => {
    const browse = browseLocation?.location;
    const p = item?.pickup;
    if (!browse || !p?.lat || !p?.lng) return null;
    return haversineMiles(browse.lat, browse.lng, p.lat, p.lng);
  }, [browseLocation?.location, item?.pickup]);

  const mapCenter = useMemo(() => {
    const p = item?.pickup;
    if (!p?.lat || !p?.lng) return null;
    return approximateTownCenter(p.lat, p.lng);
  }, [item?.pickup]);

  if (item === undefined) {
    return (
      <div className="content-page">
        <div className="detail-skeleton" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="content-page">
        <Empty
          icon="search"
          title="This item doesn't exist"
          body="It may have been deleted or the link is no longer valid."
          action={
            <Link className="primary-button" to="/">
              Back to home
            </Link>
          }
        />
      </div>
    );
  }

  const requireAuth = () => {
    if (!user) {
      onRequestAuth?.();
      return false;
    }
    return true;
  };

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const toggleSaved = async () => {
    if (!requireAuth()) return;
    const ref = doc(db, 'users', user.uid, 'saved', id);
    if (saved) {
      await deleteDoc(ref);
      await updateDoc(doc(db, 'listings', id), { saveCount: increment(-1) });
      flash('Removed from saved');
    } else {
      await setDoc(ref, { listingId: id, savedAt: serverTimestamp() });
      await updateDoc(doc(db, 'listings', id), { saveCount: increment(1) });
      flash('Saved');
    }
  };

  const toggleLiked = async () => {
    if (!requireAuth()) return;
    const ref = doc(db, 'users', user.uid, 'likes', id);
    if (liked) {
      await deleteDoc(ref);
      await updateDoc(doc(db, 'listings', id), { likeCount: increment(-1) });
    } else {
      await setDoc(ref, { listingId: id, likedAt: serverTimestamp() });
      await updateDoc(doc(db, 'listings', id), { likeCount: increment(1) });
    }
  };

  const messageSeller = async () => {
    if (!requireAuth()) return;
    if (isOwner) return nav('/sell');
    setBusy(true);
    try {
      const convId = await getOrCreateConversation({
        listingId: id,
        buyerUid: user.uid,
        sellerId: item.sellerId,
        listing: {
          title: item.title,
          price: item.price || 0,
          listingPhoto: coverPhoto(item),
        },
      });
      openMessages(convId);
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const url = shareUrl(id);
    track('share', {
      content_type: 'listing',
      item_id: id,
      method: navigator.share ? 'native' : 'clipboard',
    });
    if (navigator.share) {
      try {
        await navigator.share({ title: item.title, text: item.title, url });
        return;
      } catch {
        // fall through
      }
    }
    await navigator.clipboard?.writeText(url);
    setShareHint('Copied');
    flash('Link copied');
    setTimeout(() => setShareHint(''), 2000);
  };

  const submitReport = async (reason) => {
    if (!requireAuth()) return;
    setReportBusy(true);
    try {
      await addDoc(collection(db, 'reports'), {
        listingId: id,
        reporterId: user.uid,
        reason,
        details: '',
        status: 'open',
        createdAt: serverTimestamp(),
      });
      setReportOpen(false);
      flash('Thanks — we received your report');
    } finally {
      setReportBusy(false);
    }
  };

  const freshness = listingFreshnessBadge(item);
  const metaBits = [
    item.condition,
    item.category,
    item.quantity > 1 ? `Qty ${item.quantity}` : null,
    item.pickup?.pickupMethod,
  ].filter(Boolean);

  const gallery = (
    <div className="detail-gallery">
      <button
        type="button"
        className="detail-photo"
        onClick={() => photos.length && setViewerOpen(true)}
        aria-label="Open photo viewer"
      >
        {photos[photoIndex] ? (
          <img src={photos[photoIndex]} alt={item.title} />
        ) : (
          <div className="image-fallback">
            <Icon name="camera" size={48} />
          </div>
        )}
        {photos.length > 0 && (
          <span className="photo-hint">
            <Icon name="image" size={14} /> {photos.length > 1 ? `${photoIndex + 1}/${photos.length}` : 'View'}
          </span>
        )}
      </button>
      {photos.length > 1 && (
        <div className="photo-thumbs">
          {photos.map((src, i) => (
            <button
              key={src + i}
              type="button"
              className={i === photoIndex ? 'active' : ''}
              onClick={() => setPhotoIndex(i)}
            >
              <img src={src} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (isUnavailable) {
    return (
      <section className="detail detail--split">
        <div className="detail-nav">
          <Link className="back-link" to="/">
            <Icon name="chevronLeft" size={18} /> Home
          </Link>
        </div>
        <div className="detail-layout">
          {gallery}
          <div className="detail-info">
            <div className="detail-card">
              <div className="detail-heading">
                <h1>{item.title}</h1>
                <p className="detail-price">
                  {getPreviousPrice(item) != null && (
                    <span className="old-price">
                      {formatMoney(getPreviousPrice(item), item.currency)}
                    </span>
                  )}
                  <span className={item.isFree === true ? 'free-price' : ''}>{formatPrice(item)}</span>
                </p>
              </div>
              <div className="owner-stats">
                <span className={`status-chip status-${item.status}`}>
                  {listingStatusLabel(item.status)}
                </span>
              </div>
              <p className="description-text">{unavailableMessage}</p>
              <Link className="primary-button full" to="/">
                Back to home
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="detail detail--split">
      <div className="detail-nav">
        <button type="button" className="back-link" onClick={() => nav(-1)}>
          <Icon name="chevronLeft" size={18} /> Back
        </button>
        <div className="detail-nav-actions">
          <div className="menu-wrap">
            <button
              type="button"
              className="icon-button outlined"
              aria-label="More"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <Icon name="more" />
            </button>
            {menuOpen && (
              <div className="menu-pop">
                {!isOwner && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setReportOpen(true);
                    }}
                  >
                    <Icon name="flag" size={16} /> Report listing
                  </button>
                )}
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      nav(`/sell/edit/${id}`);
                    }}
                  >
                    <Icon name="edit" size={16} /> Edit listing
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="detail-layout">
        {gallery}

        <div className="detail-info">
          <div className="detail-card detail-card--hero">
            <div className="detail-heading">
              <h1>{item.title}</h1>
              <p className="detail-price">
                {getPreviousPrice(item) != null && (
                  <span className="old-price">
                    {formatMoney(getPreviousPrice(item), item.currency)}
                  </span>
                )}
                <span className={item.isFree === true ? 'free-price' : ''}>{formatPrice(item)}</span>
              </p>
            </div>

            {(freshness || isHighDemand(item) || item.supportsShipping) && (
              <div className="detail-badges">
                {freshness ? (
                  <span className={`badge badge-${freshness.replaceAll('_', '-')}`}>
                    {listingFreshnessLabel(freshness)}
                  </span>
                ) : null}
                {isHighDemand(item) && <span className="badge badge-demand">High demand</span>}
                {item.supportsShipping && (
                  <span className="badge badge-ship">
                    <Icon name="shipping" size={12} /> Shipping available
                  </span>
                )}
              </div>
            )}

            <p className="detail-location">
              <Icon name="mapPin" size={16} />
              <span>
                {item.pickup?.label || 'Local pickup'}
                {distance != null && ` · ${formatDistance(distance)}`}
                {` · ${relativeTime(item.createdAt)}`}
              </span>
            </p>

            {metaBits.length > 0 && (
              <div className="detail-meta-line">{metaBits.join(' · ')}</div>
            )}

            {!isOwner && (
              <>
                <div className="detail-cta-block">
                  <button
                    type="button"
                    className="primary-button full detail-message-btn"
                    onClick={messageSeller}
                    disabled={busy}
                  >
                    <Icon name="message" /> {busy ? 'Opening…' : 'Message seller'}
                  </button>
                  <div className="detail-secondary-actions">
                    <button
                      type="button"
                      className={`detail-icon-action ${liked ? 'is-on liked' : ''}`}
                      onClick={toggleLiked}
                      aria-pressed={liked}
                    >
                      <Icon name="heart" size={18} />
                      <span>{liked ? 'Liked' : 'Like'}</span>
                    </button>
                    <button
                      type="button"
                      className={`detail-icon-action ${saved ? 'is-on saved' : ''}`}
                      onClick={toggleSaved}
                      aria-pressed={saved}
                    >
                      <Icon name="bookmark" size={18} />
                      <span>{saved ? 'Saved' : 'Save'}</span>
                    </button>
                    <button type="button" className="detail-icon-action" onClick={share}>
                      <Icon name="share" size={18} />
                      <span>{shareHint || 'Share'}</span>
                    </button>
                  </div>
                </div>
              </>
            )}

            {isOwner && (
              <div className="owner-panel">
                <div className="owner-panel-head">
                  <span className={`status-chip status-${item.status}`}>
                    {listingStatusLabel(item.status)}
                  </span>
                  <button
                    type="button"
                    className="secondary-button owner-edit-btn"
                    onClick={() => nav(`/sell/edit/${id}`)}
                  >
                    <Icon name="edit" size={16} /> Edit listing
                  </button>
                </div>
                <div className="owner-metrics">
                  <div className="owner-metric">
                    <Icon name="eye" size={16} />
                    <strong>{item.viewCount || 0}</strong>
                    <span>Views</span>
                  </div>
                  <div className="owner-metric">
                    <Icon name="heart" size={16} />
                    <strong>{item.likeCount || 0}</strong>
                    <span>Likes</span>
                  </div>
                  <div className="owner-metric">
                    <Icon name="bookmark" size={16} />
                    <strong>{item.saveCount || 0}</strong>
                    <span>Saves</span>
                  </div>
                </div>
                {item.rejectionReason && (
                  <p className="form-error owner-reject">Rejected: {item.rejectionReason}</p>
                )}
              </div>
            )}
          </div>

          <div className="detail-card">
            <h3>About this item</h3>
            <p className="description-text">
              {item.description || 'No description provided.'}
            </p>
          </div>

          {mapCenter && (
            <div className="detail-card detail-map-card">
              <div className="detail-map-head">
                <div>
                  <h3>Pickup area</h3>
                  <p className="muted">Approximate city-level location only.</p>
                </div>
                <a
                  className="text-link"
                  href={googleMapsDirectionsUrl(mapCenter.lat, mapCenter.lng)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open maps
                </a>
              </div>
              <div className="map-frame-wrap">
                <PickupMap lat={mapCenter.lat} lng={mapCenter.lng} />
              </div>
            </div>
          )}
        </div>
      </div>

      {!isOwner && (
        <div className="detail-mobile-bar">
          <button
            type="button"
            className={`detail-icon-action compact ${liked ? 'is-on liked' : ''}`}
            onClick={toggleLiked}
            aria-label={liked ? 'Unlike' : 'Like'}
          >
            <Icon name="heart" size={18} />
          </button>
          <button
            type="button"
            className={`detail-icon-action compact ${saved ? 'is-on saved' : ''}`}
            onClick={toggleSaved}
            aria-label={saved ? 'Unsave' : 'Save'}
          >
            <Icon name="bookmark" size={18} />
          </button>
          <button
            type="button"
            className="primary-button detail-mobile-message"
            onClick={messageSeller}
            disabled={busy}
          >
            <Icon name="message" /> {busy ? 'Opening…' : 'Message'}
          </button>
        </div>
      )}

      <ImageViewer
        open={viewerOpen}
        photos={photos}
        index={photoIndex}
        alt={item.title}
        onClose={() => setViewerOpen(false)}
        onIndexChange={setPhotoIndex}
      />

      <Sheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        title="Report listing"
        subtitle="Tell us what’s wrong. We review every report."
      >
        <div className="report-list">
          {REPORT_REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              className="report-option"
              disabled={reportBusy}
              onClick={() => submitReport(reason)}
            >
              {reason}
            </button>
          ))}
        </div>
      </Sheet>

      {toast && <div className="toast">{toast}</div>}
      {menuOpen && (
        <button
          type="button"
          className="menu-dismiss"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </section>
  );
}
